import React, {
  useState,
  useEffect,
  useRef,
  useContext,
  useLayoutEffect
} from "react";
import ReactDOM from "react-dom";
import {
  MeetingContextState,
  MeetingModalContext,
  sessionStatusValues
} from "contextApis/meetingModalContext";
import Draggable from "react-draggable";
import styled from "styled-components";
import { Button, Card, Input, message, Spin } from "antd";
import Form from "antd/lib/form";
import {
  CloseOutlined,
  FullscreenOutlined,
  InfoCircleOutlined,
  MinusOutlined
} from "@ant-design/icons";
import {
  PatientContext,
  PatientContextState
} from "contextApis/patientContext";
import { UserContext, UserContextState } from "contextApis/userContext";
import { useMutation, useQuery } from "react-query";
import { createApiClient } from "apiClient";
import { CallRecordDuration } from "pages/callRecords/formInputs/CallRecordDuration";
import { CallTypes } from "pages/callRecords/callRecords";
import { CallRecordDate } from "pages/callRecords/formInputs/CallRecordDate";
import { CallRecordDiscussionItems } from "pages/callRecords/formInputs/CallRecordDiscussionItems";
import { CallRecordOtherIssues } from "pages/callRecords/formInputs/CallRecordOtherIssues";
import { AppButton } from "components/button";
import { ButtonType } from "components/button/appButton";
import { AppModal } from "components/modal";
import { CallRecordBody } from "sharedTypes";
import moment from "moment";
import { useLocation } from "react-router";
import { keyframes } from "styled-components";
import { Colors } from "helpers/colors";
import { css } from "styled-components";
import { zIndex } from "styled-system";

interface PutCallRecordProps {
  patientId: string;
  callId: string | number;
  body: CallRecordBody;
}

const FinalizeCallModalElement: HTMLElement | null =
  document.getElementById("modal-dom");

const FinalizeCallModal: React.FC = () => {
  if (!FinalizeCallModalElement) {
    return null;
  }
  const [form] = Form.useForm();
  const meetingContext = useContext<MeetingContextState>(MeetingModalContext);
  const patientContext = useContext<PatientContextState>(PatientContext);
  const userContext = useContext<UserContextState>(UserContext);
  const [selectedDraft, setSelectedDraft] = useState<any>();
  const [isMinimized, setIsMinimized] = useState(false);
  const [borderRed, setBorderRed] = useState(false);
  const [submissionData, setSubmissionData] = useState<any>();
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [status, setStatus] = useState<string>("");
  const [userCalled, setUserCalled] = useState<boolean>(false);
  const [isRunning, setIsRunning] = useState(false);
  const timerRef = useRef<any>(null);
  const stopTimeoutRef = useRef<any>(null);
  const [otherVales, setOtherValues] = useState<any>();
  const timeRef = useRef(0);
  const [_, forceUpdate] = useState(0); // Force re-render if needed
  const additionalTimeRef = useRef(0);
  const [selectedOptions, setSelectedOptions] = useState<any>();

  const callFinalizingPopUp = () => {
    meetingContext.setShowUpdateCallDetailsPopup(true);
    meetingContext.setSessionStatus(sessionStatusValues.NOTSTARTED);
  };

  const path = useLocation();

  const formattedTime = (minutes: any) => {
    const fullMinutes = Math.floor(minutes);
    const seconds = Math.round((minutes - fullMinutes) * 60);
    const formattedSeconds = seconds.toString().padStart(2, "0");
    return `${fullMinutes}:${formattedSeconds}`;
  };

  /* ↓↓ API CALLS ↓↓ */

  const {
    isFetching: isFetchingCallRecords,
    refetch: getCallRecords,
    data: getCallRecordsData
  } = useQuery<any>("call-records", async () => {
    const response = await createApiClient().getCallRecords(
      patientContext.patientData?.internal_id
        ? patientContext.patientData.internal_id
        : "",
      true
    );

    const latestCallRecord: any = response?.map(x => {
      return {
        id: x.id,
        date: x.date_p,
        itemsDiscussed: x.notes,
        duration: x.call_duration,
        caller: "",
        other: x.desc,
        callType: x.call_type
      };
    })[0];

    if (meetingContext.sessionStatus === sessionStatusValues.ENDED) {
      latestCallRecord?.duration < 59
        ? !userContext.isProviderModal &&
          meetingContext.setShowAddCallConfirmationModal(true)
        : !userContext.isProviderModal && callFinalizingPopUp();
    }
    return response;
  });

  const { data: callRecordNotesData, isLoading: isLoadingCallRecordNotes } =
    useQuery("call-record-notes", async () => {
      return await createApiClient().getCallRecordNotes();
    });

  useEffect(() => {
    if (getCallRecordsData) {
      const latestCallRecord: any = getCallRecordsData?.map((x: any) => {
        return {
          id: x.id,
          date: moment.utc(x.date_p).local(),
          itemsDiscussed: x.notes,
          duration: x.call_duration,
          caller: "",
          other: x.desc,
          callType: x.call_type
        };
      })[0];

      setSelectedDraft(latestCallRecord);
    }
  }, [getCallRecordsData]);

  useEffect(() => {
    form.setFieldsValue({ id: selectedDraft?.id });
  }, [selectedDraft]);

  useEffect(() => {
    if (
      meetingContext.fetchCallRecords &&
      meetingContext.sessionStatus === sessionStatusValues.ENDED
    ) {
      setTimeout(() => {
        getCallRecords();
      }, 500);
    }
  }, [
    meetingContext.fetchCallRecords,
    getCallRecordsData,
    meetingContext.sessionStatus
  ]);

  /* ↓↓ UTILITY FUNCTION ↓↓ */

  function convertToSeconds(timeStr: any) {
    if (!timeStr?.includes(":")) {
      const minutes = Number(timeStr);
      return minutes * 60;
    }
    const [minutes, seconds] = timeStr.split(":").map(Number);
    return minutes * 60 + seconds + timeRef.current + additionalTimeRef.current;
  }

  const handleSubmit = () => {
    if (form?.getFieldValue("date")?.date === undefined) return;
    const noDate = !form?.getFieldValue("date")?.date;
    const noDuration = !form?.getFieldValue("duration")?.duration || "";
    const noDiscussionItems =
      form?.getFieldValue("discussionItems")?.discussionItems <= 0 || "";
    const noOtherIssues =
      !form?.getFieldValue("otherIssues")?.otherIssues || "";

    if (noDate && noDuration && noDiscussionItems && noOtherIssues) {
      setErrorMessage("The form is empty, please enter a value to proceed.");
      return;
    }
    setErrorMessage("");

    setSubmissionData({
      id: form?.getFieldValue("id") || "",
      date: form?.getFieldValue("date")?.date || "",
      duration: form?.getFieldValue("duration").duration || "",
      discussionItems:
        form?.getFieldValue("discussionItems")?.discussionItems || "",
      otherIssues: form?.getFieldValue("otherIssues")?.otherIssues || ""
    });
  };

  const handleOnFinish = (values: any) => {
    if (!values) return;
    const callRecordBody: CallRecordBody = {
      callDateTime: moment.utc(values?.date).format("MM-DD-YYYY HH:mm"),
      callLength: convertToSeconds(values?.duration),
      notes: [
        ...values.discussionItems.map((item: any) => ({
          desc: item.note_detail,
          id: item.id.toString(),
          isSelected: "true"
        })),
        { desc: values.otherIssues, id: "999" }
      ],
      patient_id: patientContext.patientData?.id.toString() || "",
      provider_id: userContext.userData?.internal_id.toString() || "",
      typeOfCall: selectedDraft?.callType ? selectedDraft?.callType : "Manual",
      status: status !== "" ? status : "DRAFT"
      // status: path.pathname === "/patients" ? "DRAFT" : "COMPLETED"
    };
    if (userCalled) {
      putCallRecord({
        patientId: patientContext.patientData?.id.toString() || "",
        callId: values.id || "",
        body: callRecordBody
      });
    }
  };

  useEffect(() => {
    if (path.pathname === "/patients") {
      form.submit();
      handleSubmit();
    }
  }, [path.pathname]);

  useEffect(() => {
    if (submissionData) {
      if (path.pathname === "/patients") {
        handleOnFinish(submissionData);
        setSubmissionData(null);
      }
    }
  }, [submissionData]);

  useLayoutEffect(() => {
    if (meetingContext.showUpdateCallDetailsPopup === true) {
      timeRef.current = 0;
      additionalTimeRef.current = 0;
      setUserCalled(true);
      setBorderRed(false);
      startTimerFunc(); // Start timer when popup is shown
    }
  }, [meetingContext.showUpdateCallDetailsPopup, userCalled]);

  useEffect(() => {
    return () => {
      if (meetingContext.showUpdateCallDetailsPopup && userCalled) {
        if (submissionData) {
          // handleOnFinish(submissionData);
          setSubmissionData(undefined);
        }
        setSelectedDraft(undefined);
        form.resetFields();
        meetingContext.setShowUpdateCallDetailsPopup(false);
      }
    };
  }, [meetingContext.showUpdateCallDetailsPopup, userCalled, form]);

  useEffect(() => {
    if (meetingContext.showUpdateCallDetailsPopup === false) {
      resetTimer();
    }
  }, [meetingContext.showUpdateCallDetailsPopup]);

  /* ↓↓ API CALLS ↓↓ */

  const { mutate: putCallRecord } = useMutation(
    "edit-call-record",
    async (props: PutCallRecordProps) => {
      /* eslint-disable */
      createApiClient().putCallRecord(
        props.patientId,
        props.callId,
        props.body
      );
      /* eslint-enable */
    },
    {
      onSuccess: () => {
        resetTimer();
        setUserCalled(false);
        message.success("Call record details saved");
        meetingContext.setShowUpdateCallDetailsPopup(false);
        setTimeout(() => {
          getCallRecords();
          meetingContext.setRefreshCallRecordsStatus(true);
        }, 500);
      },
      onError: () => {
        message.error("There was a problem adding call record");
      }
    }
  );

  const startTimerFunc = () => {
    // Clear any existing timeout first
    if (stopTimeoutRef.current) {
      clearTimeout(stopTimeoutRef.current);
    }

    if (!isRunning) {
      // Only initialize the timer if it's not already running
      const startTime = Date.now() - timeRef.current * 1000; // Account for existing time
      timerRef.current = setInterval(() => {
        timeRef.current = Math.floor((Date.now() - startTime) / 1000);
        forceUpdate(prev => prev + 1);
      }, 1000);
      setIsRunning(true);
    }

    // Always reset the auto-stop timeout when there's activity
    stopTimeoutRef.current = setTimeout(() => {
      clearInterval(timerRef.current);
      setIsRunning(false);
    }, 120000);
  };

  const resetTimer = () => {
    setBorderRed(false);
    clearInterval(timerRef.current);
    clearTimeout(stopTimeoutRef.current);
    setIsRunning(false);
    timeRef.current = 0;
    forceUpdate(prev => prev + 1); // Ensure UI updates
  };

  const formatTime = () => {
    const totalSeconds = timeRef.current;
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(
      2,
      "0"
    )}`;
  };

  function Clicked() {
    if (userCalled) {
      startTimerFunc();
    }
  }

  useEffect(() => {
    if (userCalled) {
      startTimerFunc();
    }
  }, [otherVales]);

  useEffect(() => {
    if (isRunning) {
      setBorderRed(false);
    } else {
      setBorderRed(true);
    }
  }, [isRunning]);

  return ReactDOM.createPortal(
    <>
      {meetingContext.showUpdateCallDetailsPopup && (
        <div onClick={Clicked}>
          <Draggable
            handle=".drag-handle"
            bounds="body"
            defaultPosition={{
              x: (window.innerWidth - (isMinimized ? 290 : 500)) / 2,
              y: (window.innerHeight - 400) / 2
            }}
            grid={[1, 1]}
          >
            <FloatingContainer isMinimized={isMinimized} borderRed={borderRed}>
              <FloatingCard
                title={
                  <div
                    className="drag-handle"
                    style={{ cursor: "grab", textAlign: "left" }}
                  >
                    Add Call Details <span>{formatTime()}</span>
                  </div>
                }
                extra={
                  <>
                    <Button
                      type="text"
                      icon={
                        isMinimized ? <FullscreenOutlined /> : <MinusOutlined />
                      }
                      onClick={() => setIsMinimized(!isMinimized)}
                    />
                    <Button
                      type="text"
                      icon={<CloseOutlined />}
                      onClick={() => {
                        setStatus("DRAFT");
                        form.submit();
                        handleSubmit();
                      }}
                    />
                  </>
                }
                style={{ width: "100%", height: "100%" }}
              >
                {/* Keep the form elements in the DOM but hide them with CSS when minimized */}
                <OverflowCallRecordCardContainer
                  style={{ display: isMinimized ? "none" : "block" }}
                >
                  <AddCallRecordGridContainer>
                    <div>
                      <Form.Item
                        name="date"
                        initialValue={selectedDraft?.date_p}
                      >
                        <CallRecordDate
                          clearForm={false}
                          selectedDraft={selectedDraft}
                          form={form}
                          disabled={
                            isFetchingCallRecords ||
                            selectedDraft?.callType !== CallTypes.MANUAL
                              ? true
                              : false
                          }
                        />
                      </Form.Item>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between"
                      }}
                    >
                      <div style={{ width: "49%" }}>
                        <Form.Item
                          name="duration"
                          initialValue={formattedTime(
                            Number(selectedDraft?.duration) / 60
                          )}
                        >
                          <CallRecordDuration
                            clearForm={false}
                            selectedDraft={selectedDraft}
                            form={form}
                            disabled={
                              isFetchingCallRecords ||
                              selectedDraft?.callType !== CallTypes.MANUAL
                                ? true
                                : true
                            }
                          />
                        </Form.Item>
                      </div>
                      <div
                        style={{
                          width: "49%",
                          alignItems: "center",
                          justifyContent: "flex-start"
                        }}
                      >
                        <DurationLabelText>Minutes</DurationLabelText>
                      </div>
                    </div>
                  </AddCallRecordGridContainer>

                  <Form.Item
                    name="discussionItems"
                    initialValue={selectedDraft?.notes}
                  >
                    {isLoadingCallRecordNotes ? (
                      <Spin />
                    ) : (
                      <CallRecordDiscussionItems
                        setSelectedOptions={setSelectedOptions}
                        discussionItemOptions={callRecordNotesData || []}
                        clearForm={false}
                        selectedDraft={selectedDraft}
                        form={form}
                      />
                    )}
                  </Form.Item>
                  {selectedOptions?.some(
                    (item: any) =>
                      item.note_detail === "Reviewed outside record/EHR"
                  ) && (
                    <Form.Item
                      style={{ fontSize: 8, marginTop: -10 }}
                      name="manualTime"
                      label="Additional time for review"
                      rules={[{ required: true, message: "Please enter time" }]}
                    >
                      <Input
                        style={{ borderRadius: 6, width: "40%" }}
                        type="number"
                        placeholder="Enter time"
                        min={0}
                        onChange={e => {
                          const minutes = parseInt(e.target.value);
                          if (!isNaN(minutes)) {
                            additionalTimeRef.current = minutes * 60;
                          }
                        }}
                      />
                    </Form.Item>
                  )}
                  <Form.Item
                    name="otherIssues"
                    initialValue={selectedDraft?.desc}
                  >
                    <CallRecordOtherIssues
                      clearForm={false}
                      selectedDraft={selectedDraft}
                      form={form}
                      disabled={isFetchingCallRecords}
                      enableTypingTimer={false}
                      setOtherValues={setOtherValues}
                    />
                  </Form.Item>
                  <ErrorMessage style={{ marginTop: "-20px" }}>
                    {errorMessage}
                  </ErrorMessage>
                  <ButtonContainer>
                    <AppButton
                      type={ButtonType.Primary}
                      onClick={() => {
                        setStatus("COMPLETED");
                        form.submit();
                        handleSubmit();
                        meetingContext.updateFetchCallRecords(false);
                      }}
                      buttonContent={"Finalize"}
                      style={{ width: 120 }}
                      disabled={
                        isLoadingCallRecordNotes || isFetchingCallRecords
                      }
                    />
                  </ButtonContainer>
                </OverflowCallRecordCardContainer>
              </FloatingCard>
            </FloatingContainer>
          </Draggable>
          <AppModal
            visible={submissionData ? true : false}
            title={
              status === "COMPLETED" ? "Finalize Submission" : "Save as Draft"
            }
            style={{ zIndex: 2000 }}
            zIndex={4000}
            cancelText="No"
            okText="Yes"
            onCancel={() => {
              setSubmissionData(undefined);
            }}
            onOk={() => {
              if (submissionData) {
                handleOnFinish(submissionData);
                setSubmissionData(undefined);
              }
              setSelectedDraft(undefined);
              form.resetFields();
            }}
            prompt={{
              icon: (
                <InfoCircleOutlined
                  style={{ color: "#F5A623", fontSize: "40px" }}
                />
              ),
              text:
                status === "COMPLETED"
                  ? "You are going to create a call record that is non-editable, do you want to finalize?"
                  : "Do you want to save this call records as Draft?"
            }}
            footer
          />
        </div>
      )}
    </>,
    FinalizeCallModalElement
  );
};

const blinkAnimation = keyframes`
    0% { border-color: ${Colors.Froly} }
    50% { border-color: white;  }
    100% { border-color: ${Colors.Froly} ;  }
  `;

const FloatingContainer = styled.div<{
  isMinimized: boolean;
  borderRed: boolean;
}>`
  position: absolute;
  z-index: 1001;
  width: ${props => (props.isMinimized ? "290px" : "500px")};
  border: ${props =>
    props.borderRed ? `1.8px solid ${Colors.Froly}` : "none"};
  border-radius: 10px;
  transition: ease-in-out;
  max-width: calc(100vw - 100px);
  max-height: calc(100vh - 100px);
  overflow: hidden;
  background: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);

  // Apply animation if borderRed is true
  ${props =>
    props.borderRed &&
    css`
      animation: ${blinkAnimation} 2s infinite;
    `}
`;

const FloatingCard = styled(Card)`
  box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  border-radius: 10px;
`;

const AddCallRecordGridContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const DurationLabelText = styled.div`
  display: flex;
  padding-top: 10px;
  font-size: 14px;
`;

const OverflowCallRecordCardContainer = styled.div`
  max-height: calc(100vh - 350px);
  overflow: auto;
  padding-right: 3px;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 10px;
`;

const ErrorMessage = styled.div`
  display: grid;
  place-items: center;
  height: 100%;
  color: red;
  opacity: 0.3;
  font-weight: bold;
`;

export default FinalizeCallModal;
